import classnames from 'classnames';
import PropTypes from 'prop-types';
import {Button, Icon, IconTool} from '@gisatcz/ptr-atoms';

import chart_1 from '../../assets/img/chart_3.png';
import chart_2 from '../../assets/img/chart_4.png';
import gisat from '../../assets/img/gisat.png';
import esa from '../../assets/img/logo-esa.png';

import './style.scss';

const IntroOverlay = ({open = true, chartExpanded, hidden, changeState}) => {
	const classes = classnames('insar-IntroOverlay', {
		'is-minimised': !open,
		'is-chartExpanded': chartExpanded,
		'is-hidden': hidden,
	});

	const onAboutClick = () => {
		const element = document.getElementById('about');
		if (element) {
			element.scrollIntoView({behavior: 'smooth'});
		}
	};

	return (
		<div className={classes}>
			<div className="insar-IntroHero">
				<IconTool
					className="insar-IntroOverlay-control"
					onClick={() => changeState(!open)}
					icon="back"
				/>
				<div className="insar-Intro-Header" onClick={() => changeState(!open)}>
					<span>
						EO4SD-DRR Service Demo: EO in Support of Alaoa Multipurpose Dam
						Project, Samoa
					</span>
					<h1>Assessment of ground motions and exposure</h1>
				</div>
				<div className="insar-Intro-Cta">
					<Button
						className="go-to-map"
						inverted
						primary
						large
						onClick={() => changeState(!open)}
					>
						Go to the map
						<Icon icon="back" />
					</Button>
					<Button className="about-project" large ghost onClick={onAboutClick}>
						About project
						<Icon icon="back" />
					</Button>
				</div>
				<div className="insar-Intro-logos">
					<a
						href="https://www.esa.int/"
						target="_blank"
						rel="noreferrer noopener"
					>
						<img alt="esa logo" src={esa} />
					</a>
					<a
						className="insar-Intro-logos-gisat"
						href="http://gisat.cz/"
						target="_blank"
						rel="noreferrer noopener"
					>
						<img alt="gisat_logo" src={gisat} />
					</a>
				</div>
			</div>
			<div className="insar-Intro-Content" tabIndex={-1}>
				<div tabIndex={-1}>
					<h2 id="about">About project</h2>
					<p>
						Greater Apia on the Samoa island in the Pacific is exposed to
						regular climate hazards, including <b>flash flooding</b> in upper
						areas, <b>riverine flooding</b> in the floodplains and{' '}
						<b>coastal flooding</b> from storms and surges. It is also exposed
						to cyclone activity and droughts. Currently, extreme rainfall in the
						Vaisigano River catchment, the largest on the island, is the most
						significant single cause of flooding impacts in Apia. The river
						flows through the urban area of Apia and near to the central
						business district (CBD). Apart from Vaisigano, seven additional
						river catchments come steeply down from the mountain and cross parts
						of the city area to the ocean. Recent extreme flooding events caused
						by heavy rains or water surge from tropical cyclones have inflicted
						severe damage, mainly in the Vaisagano floodplain.
					</p>
					<p>
						A major earthquake hit the area in 2009. Subsequently, initial
						observations suggest it has triggered significant{' '}
						<b>ground subsidence</b> amounting to up to 8 mm/year in a vertical
						direction. This geohazard, which may further aggravate flood
						impacts, is supposed to be of tectonic origin associated with
						post-seismic viscoelastic relaxation. However, some models project
						that the subsidence may decelerate and be reversed in the coming
						decades
					</p>
					<p>
						The service outputs provide evidence of{' '}
						<b>ground motion patterns</b> in the areas of interest using{' '}
						<b>multi-temporal satellite SAR data analysis</b> – by{' '}
						<b>Persistent Scatterers Interferometry method (PSI)</b>. Gisat
						processed and analysed up to 6-years-long time series of radar
						measurements from the Sentinel-1 satellite mission (2015-Apr 2021).
						One stack of images from Ascending orbital trajectory of Sentinel-1
						was utilised. The results provide insight into the localisation and
						magnitude of local ground motion hot spots.
					</p>
					<p>
						The <b>Interferometric measurements panel</b> provides tools to
						visualise patterns of the average ground motion velocities. Ground
						motion velocity is measured for each measurement point (coherent
						target called <b>P</b>ersistent <b>S</b>catterer, PS) in the
						satellite’s geometry (line-of-sight, LOS) by the PSI technique.
						Furthermore, PS motion vectors were projected along other directions
						to provide insights about motion rates relevant to subsidence
						(vertical geometry) or slope instabilities (DTS). The InSAR
						measurements were calibrated using the respective GPS time series
						from the permanent GNSS station (SAMO) to estimate the combined
						effect of tectonic (absolute) and differential (relative) motions in
						the vertical direction. Following directions can be picked in the
						panel:
					</p>
					<ul>
						<li>
							<b>Line-of-sight geometry (LOS)</b> – PS motion vectors is
							presented in the direction of radar looking geometry
						</li>
						<li>
							<b>Down-the-slope geometry (DTS)</b> – PS motion vector in LOS
							direction was projected along the direction of the steepest
							terrain slope defined by local aspect and slope gradient derived
							from Copernicus DEM with 30m pixel spacing.{' '}
							<em className="warning">
								Warning: Subject to potential biases due to DEM artefacts or if
								real motion happens in a different than the projected direction
							</em>
						</li>
						<li>
							<b>Vertical geometry (projected)</b> – PS motion vector in LOS
							direction was projected along vertical direction using simple
							geometric assumption.{' '}
							<em className="warning">
								Warning: Subject to potential biases if real motion has a strong
								horizontal component.
							</em>
						</li>
						<li>
							<b>Vertical geometry (calibrated)</b> – PS motion vector in
							projected vertical geometry was further adjusted by calibrating
							relative InSAR measurements using a record of absolute motion
							measured by GPS at 1 GNSS permanent station (code: SAMO).
							<em className="warning">
								Warning: Records from only 1 GNSS station were used for
								calibration. Calibrated motion rates could be biased in other
								parts of the island.
							</em>
						</li>
					</ul>
					<img className="insar-Intro-chartImage" alt="chart" src={chart_1} />
					<img className="insar-Intro-chartImage" alt="chart" src={chart_2} />
					<p className="insar-Intro-chartDescription">
						* Displacement time series from 19 PSI points (coh &gt; 0.7, within
						&lt; 300m radius from SAMO) filtered (after projection from LOS to
						VertG) using moving average to reduce noise. Filtered time-series
						calibrated using mean annual displacement rate from SAMO GPS (-5 mm
						/ year) as of 2015-2019.
					</p>
					<p className="insar-Intro-chartDescription">
						** NGL14 GPS daily solutions from SAMO permanent GNSS station,
						measured vertical (up) displacement component. Source: available
						online at{' '}
						<a
							href="https://www.sonel.org/spip.php?page=gps&idStation=831"
							target="_blank"
							rel="noreferrer noopener"
						>
							https://www.sonel.org/spip.php?page=gps&idStation=831
						</a>
					</p>
					<p className="insar-Intro-chartDescription">
						*** Smoothed 15-day average from NGL14 GPS daily solutions, no
						correction
					</p>
				</div>
			</div>
			<div className="insar-Intro-Footer">
				<div className="insar-Intro-Footer-copyrights">
					© {`${new Date().getFullYear()}`}{' '}
					<a
						href="http://gisat.cz/content/en"
						target="_blank"
						rel="noreferrer noopener"
					>
						Gisat
					</a>
					. European Space Agency, EO4SD Disaster Risk Reduction cluster (
					{`${new Date().getFullYear()}`})
				</div>
			</div>
		</div>
	);
};

IntroOverlay.propTypes = {
	open: PropTypes.bool,
	chartExpanded: PropTypes.bool,
	hidden: PropTypes.bool,
	changeState: PropTypes.func,
};

export default IntroOverlay;
